import { getUserErrors } from "common-client/utils/apollo";
import { useDeleteTaskMutation } from "../../generated/graphql";
import { useConfirmDestructiveAction } from "../../hooks/useConfirmDestructiveAction";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { Body } from "../Common/Typography";
import { GET_TASKS, type Task } from "./__queries__";

export function useDeleteTaskWithConfirmation({
  task,
  onDelete,
}: {
  task: Pick<Task, "id" | "name">;
  onDelete?: () => void;
}) {
  const toast = useStatusToasts();

  const [deleteTask, { loading }] = useDeleteTaskMutation({
    refetchQueries: [GET_TASKS],
    onCompleted: () => {
      toast.addSuccessToast("Task deleted");
    },
    onError: error => {
      const userError = getUserErrors(error);

      if (userError) {
        toast.addErrorToast(userError);
      } else {
        toast.addErrorToast(
          "An error occurred while deleting the task. Please try again.",
        );
      }
    },
  });

  return useConfirmDestructiveAction({
    title: "Delete task",
    subtitle: task.name,
    content: (
      <Body size="large" type="regular">
        Are you sure you would like to delete this task? Associated record types
        will not be deleted.{" "}
        <Body size="large" type="emphasis" as="span">
          This action cannot be undone.
        </Body>
      </Body>
    ),
    onConfirm: async hideModal => {
      await deleteTask({ variables: { id: task.id } });
      onDelete?.();
      // the modal will be removed from the DOM once the GetTasks query is
      // refetched (and the task is removed from the cache), but that may take
      // an indeterminate amount of time, so explicitly hide the modal after we
      // know the deletion operation completed, successfully or not
      hideModal();
    },
    loading,
  });
}
